import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { User } from '../models/user.model';

export interface UserFilters {
  role?: string;
  activo?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface UserListResponse {
  users: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UserStatistics {
  total: number;
  active: number;
  inactive: number;
  byRole: { [key: string]: number };
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = `${environment.apiUrl}/users`;

  constructor(private http: HttpClient) { }

  /**
   * Get all users with filters
   */
  getUsers(filters: UserFilters = {}): Observable<UserListResponse> {
    let params = new HttpParams();

    if (filters.role) params = params.set('role', filters.role);
    if (filters.activo !== undefined) params = params.set('activo', filters.activo.toString());
    if (filters.search) params = params.set('search', filters.search);
    if (filters.page) params = params.set('page', filters.page.toString());
    if (filters.limit) params = params.set('limit', filters.limit.toString());

    return this.http.get<UserListResponse>(this.apiUrl, { params });
  }

  /**
   * Get user by ID
   */
  getUser(id: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create new user
   */
  createUser(userData: Partial<User> & { password: string }): Observable<User> {
    return this.http.post<User>(this.apiUrl, userData);
  }

  /**
   * Update user
   */
  updateUser(id: number, userData: Partial<User>): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/${id}`, userData);
  }

  /**
   * Delete user (soft delete)
   */
  deleteUser(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${id}`);
  }

  /**
   * Reactivate user
   */
  reactivateUser(id: number): Observable<User> {
    return this.http.post<User>(`${this.apiUrl}/${id}/reactivate`, {});
  }

  /**
   * Reset user password (admin action)
   */
  resetPassword(id: number, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${id}/reset-password`, { newPassword });
  }

  /**
   * Get user statistics
   */
  getStatistics(): Observable<UserStatistics> {
    return this.http.get<UserStatistics>(`${this.apiUrl}/statistics`);
  }
}